const TRUTHY_VALUES = ['true', 'yes', 'on'];
const SMART_COMPONENT_EVENT_OPTIONS = {
    bubbles: true,
    detail: { fromSmartComponents: true }
};
export const NATIVE_FORM_CONTROLS = ['input', 'textarea', 'select'];
export function isHidden(element) {
    const computedStyle = window.getComputedStyle(element);
    return (element.type === 'hidden' ||
        computedStyle.display === 'none' ||
        computedStyle.visibility === 'hidden');
}
export function isNativeFormControl(element) {
    return (element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement);
}
export function getFieldDescription(form, element, isCustomInput = false) {
    const fieldElement = isCustomInput
        ? (element.querySelector(NATIVE_FORM_CONTROLS.join(',')) ?? element)
        : element;
    const smartPasteDescription = fieldElement.getAttribute('data-smartpaste-description');
    if (smartPasteDescription) {
        return smartPasteDescription;
    }
    if (fieldElement.id) {
        const labels = form.querySelectorAll(`label[for='${fieldElement.id}']`);
        if (labels.length === 1) {
            const labelText = labels[0].textContent?.trim();
            if (labelText) {
                return labelText;
            }
        }
    }
    return fieldElement.getAttribute('name') ?? fieldElement.id ?? null;
}
export function isCustomInput(element, options) {
    if (!options?.customInputs || !Array.isArray(options.customInputs)) {
        return false;
    }
    return options.customInputs.some((customInput) => element.matches(customInput.identifier) || element.closest(customInput.identifier));
}
export function isRadioElement(element) {
    return element instanceof HTMLInputElement && element.type === 'radio';
}
export function getNativeFieldType(element) {
    if (element.type === 'checkbox') {
        return 'boolean';
    }
    if (element.type === 'number') {
        return 'number';
    }
    if (element instanceof HTMLSelectElement || isRadioElement(element)) {
        return 'fixed-choices';
    }
    return 'string';
}
export function getSelectElementAllowedValues(element) {
    return Array.from(element.querySelectorAll('option'))
        .filter((option) => Boolean(option.value))
        .map((option) => option.textContent ?? '');
}
export function getRadioElementAllowedValues(identifier, formElement) {
    const radioInputs = Array.from(formElement.querySelectorAll('input[type=radio]'));
    return radioInputs
        .filter((input) => input.name === identifier)
        .map((input) => getFieldDescription(formElement, input))
        .filter((description) => description !== null);
}
export function getInputsSelector(kendoInputs) {
    const nativeControls = NATIVE_FORM_CONTROLS.join(',');
    if (!Array.isArray(kendoInputs) || kendoInputs.length === 0) {
        return nativeControls;
    }
    const kendoSelectors = kendoInputs.map((input) => input.identifier).join(',');
    return `${nativeControls},${kendoSelectors}`;
}
export function findInputRadioByText(form, radioGroupName, valueText) {
    const radioInputs = Array.from(form.querySelectorAll('input[type=radio]'));
    const candidates = radioInputs
        .filter((input) => input.name === radioGroupName)
        .map((input) => ({
        element: input,
        description: getFieldDescription(form, input)
    }));
    const exactMatch = candidates.find((candidate) => candidate.description === valueText);
    if (exactMatch) {
        return exactMatch.element;
    }
    const partialMatches = candidates.filter((candidate) => candidate.description && candidate.description.includes(valueText));
    return partialMatches.length === 1 ? partialMatches[0].element : null;
}
export function setFormElementValueWithEvents(elem, value) {
    if (elem instanceof HTMLSelectElement) {
        updateSelectElement(elem, value.toString());
    }
    else if (elem instanceof HTMLInputElement &&
        (elem.type === 'radio' || elem.type === 'checkbox')) {
        updateCheckableInput(elem, value);
    }
    else if (elem instanceof HTMLInputElement || elem instanceof HTMLTextAreaElement) {
        updateTextInput(elem, value);
    }
}
function isTruthyValue(value) {
    const valueString = value?.toString().toLowerCase();
    return TRUTHY_VALUES.includes(valueString);
}
function updateSelectElement(elem, value) {
    const newSelectedIndex = findSelectOptionByText(elem, value);
    if (newSelectedIndex !== null && elem.selectedIndex !== newSelectedIndex) {
        notifyFormElementBeforeWritten(elem);
        elem.selectedIndex = newSelectedIndex;
        notifyFormElementWritten(elem);
    }
}
function updateCheckableInput(elem, value) {
    const shouldCheck = isTruthyValue(value);
    if (elem.checked !== shouldCheck) {
        notifyFormElementBeforeWritten(elem);
        elem.checked = shouldCheck;
        notifyFormElementWritten(elem);
    }
}
function formatDateValue(stringValue) {
    const dateObj = new Date(stringValue);
    return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : stringValue;
}
function updateTextInput(elem, value) {
    const stringValue = value?.toString() ?? '';
    const finalValue = elem instanceof HTMLInputElement && elem.type === 'date'
        ? formatDateValue(stringValue)
        : stringValue;
    if (elem.value !== finalValue) {
        notifyFormElementBeforeWritten(elem);
        elem.value = finalValue;
        notifyFormElementWritten(elem);
    }
}
function findSelectOptionByText(selectElem, valueText) {
    const options = Array.from(selectElem.querySelectorAll('option'));
    const exactMatch = options.find((option) => option.textContent === valueText);
    if (exactMatch) {
        return options.indexOf(exactMatch);
    }
    const partialMatches = options.filter((option) => option.textContent && option.textContent.includes(valueText));
    return partialMatches.length === 1 ? options.indexOf(partialMatches[0]) : null;
}
function notifyFormElementBeforeWritten(elem) {
    elem.dispatchEvent(new CustomEvent('beforeinput', SMART_COMPONENT_EVENT_OPTIONS));
}
function notifyFormElementWritten(elem) {
    elem.dispatchEvent(new CustomEvent('input', SMART_COMPONENT_EVENT_OPTIONS));
    elem.dispatchEvent(new CustomEvent('change', SMART_COMPONENT_EVENT_OPTIONS));
}
